import { generatePath, useNavigate } from 'react-router-dom';

import { ButtonProps } from '@components/button';
import { IconBuilder } from '@components/icons/builder';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconTrash } from '@components/icons/trash';
import { IconUpload } from '@components/icons/upload';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { JourneyType } from 'api/types';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';

import { useDeleteJourneyModal } from './modals/delete-journey';
import { useDuplicateJourneyModal } from './modals/duplicate-journey';
import { useMoveJourneyModal } from './modals/move';

import { FavoriteMenuItem } from '../../favorites/favorites';
import { useBulkImportJourneyTravellersModal } from '../bulk-import/journey-travellers';

type JourneyMenuProps = {
  journey: JourneyType;
  variant?: ButtonProps['variant'];
};

export const JourneyMenu = ({ journey, variant = 'secondary' }: JourneyMenuProps) => {
  const { t } = useScopedTranslate(locale);
  const { canManageJourneyTravellers } = usePermissions();

  const showDuplicateJourneyModal = useDuplicateJourneyModal();
  const showBulkImportModal = useBulkImportJourneyTravellersModal();
  const showDeleteJourneyModal = useDeleteJourneyModal();
  const showMoveJourneyModal = useMoveJourneyModal();

  const navigate = useNavigate();

  return (
    <Menu
      buttonProps={{
        dataCy: 'journey-menu-btn',
        variant,
        iconRight: <IconMoreVertical />,
      }}
    >
      <Menu.Item
        onSelect={() =>
          navigate(
            generatePath(routePaths.editJourney, {
              workspaceId: journey.workspace,
              journeyId: journey.shortId,
              '*': '',
            })
          )
        }
        label={t('openJourneyBuilder')}
        icon={<IconBuilder />}
      />
      <Menu.Item
        onSelect={() => showDuplicateJourneyModal({ journey })}
        label={t('duplicateJourney')}
        icon={<IconDuplicate />}
      />
      <Menu.Item
        onSelect={() => showMoveJourneyModal({ journey })}
        dataCy="move-journey"
        label={t('moveJourney')}
        icon={<IconMove />}
      />
      <FavoriteMenuItem shortId={journey.shortId} contentType="journey" />
      {canManageJourneyTravellers && journey.hasPublishedJourney && (
        <Menu.Item
          onSelect={() => showBulkImportModal({ journeyId: journey.shortId })}
          label={t('importTravellers')}
          icon={<IconUpload />}
        />
      )}
      <Menu.Separator />
      <Menu.Item
        onSelect={() => showDeleteJourneyModal({ journey })}
        dataCy="delete-journey"
        variant="danger"
        label={t('deleteJourney')}
        icon={<IconTrash />}
      />
    </Menu>
  );
};

export const locale = {
  en: {
    openJourneyBuilder: 'Open Journey Builder',
    duplicateJourney: 'Duplicate journey',
    deleteJourney: 'Delete journey',
    moveJourney: 'Move to workspace',
    importTravellers: 'Import travellers (csv)',
  },
};
